/**
 * Admin — Founder Bonus Campaign tracker.
 *
 * Shows live progress of the first-50 founder campaign and lets admins flip
 * each founder's gift-box status (pending → sent → delivered) plus add a
 * private note (carrier, tracking, etc.).
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Gift, RefreshCw, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { getToken } from '@/lib/adminApi';

const API = process.env.REACT_APP_BACKEND_URL || '';
const CAP = 50;

const STATUS_OPTS = [
  { value: 'pending',   label: 'Pending',   tone: 'bg-amber-100 text-amber-900 border-amber-300' },
  { value: 'sent',      label: 'Sent',      tone: 'bg-sky-100 text-sky-900 border-sky-300' },
  { value: 'delivered', label: 'Delivered', tone: 'bg-emerald-100 text-emerald-900 border-emerald-300' },
];

async function adminCall(path, opts = {}) {
  const t = getToken();
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
      Authorization: t ? `Bearer ${t}` : '',
    },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.detail || `${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

function fmtDate(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return s;
  }
}

export default function AdminFounderCampaign() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState({});      // { [user_id]: { status, note, dirty } }
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const items = await adminCall('/api/admin/founder-campaign');
      setRows(items || []);
      // Seed editable drafts so user inputs persist while typing.
      setDraft(
        Object.fromEntries(
          (items || []).map((r) => [r.user_id, { status: r.status, note: r.note || '', dirty: false }]),
        ),
      );
    } catch (e) {
      setError(e.message || 'Failed to load founders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setField = (uid, field, value) => {
    setDraft((d) => ({
      ...d,
      [uid]: { ...(d[uid] || {}), [field]: value, dirty: true },
    }));
  };

  const save = async (uid) => {
    const d = draft[uid];
    if (!d) return;
    setSavingId(uid);
    try {
      const updated = await adminCall(`/api/admin/founder-campaign/${uid}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: d.status, note: d.note }),
      });
      setRows((r) => r.map((row) => (row.user_id === uid ? updated : row)));
      setDraft((dr) => ({ ...dr, [uid]: { ...dr[uid], dirty: false } }));
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSavingId(null);
    }
  };

  const claimed = rows.length;
  const remaining = Math.max(CAP - claimed, 0);
  const pctFull = Math.round((claimed / CAP) * 100);
  const stats = STATUS_OPTS.map((s) => ({
    ...s,
    n: rows.filter((r) => r.status === s.value).length,
  }));

  return (
    <div className="space-y-6" data-testid="admin-founder-campaign-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#05223D] flex items-center gap-2.5">
            <Gift className="h-6 w-6 text-[#F25C05]" />
            Founder Bonus Campaign
          </h1>
          <p className="text-sm text-[#465B70] mt-1">
            First 50 registered customers receive a SmartPaw Gift Box. Track and update gift-box fulfilment here.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="founder-refresh-button"
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Top counter cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-[#F25C05]/30 bg-[#FFF6EE]" data-testid="founder-stat-claimed">
          <CardContent className="p-5">
            <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#F25C05]">Claimed</p>
            <p className="font-display font-extrabold text-3xl text-[#05223D] mt-1 tabular-nums">
              {claimed} <span className="text-base font-bold text-[#465B70]">/ {CAP}</span>
            </p>
            <div className="mt-3 h-2 w-full rounded-full bg-[#F25C05]/15 overflow-hidden">
              <div
                className="h-full rounded-full bg-[#F25C05] transition-all"
                style={{ width: `${pctFull}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-[#465B70]">{pctFull}% full</p>
          </CardContent>
        </Card>

        <Card data-testid="founder-stat-remaining">
          <CardContent className="p-5">
            <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#0A4D8C]">Remaining</p>
            <p className="font-display font-extrabold text-3xl text-[#05223D] mt-1 tabular-nums">
              {remaining}
            </p>
            <p className="mt-2 text-xs text-[#465B70]">
              {remaining === 0 ? 'Campaign closed — all spots claimed.' : 'Founder spots still available.'}
            </p>
          </CardContent>
        </Card>

        {stats.map((s) => (
          <Card key={s.value} data-testid={`founder-stat-${s.value}`}>
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#465B70]">
                {s.label}
              </p>
              <p className="font-display font-extrabold text-3xl text-[#05223D] mt-1 tabular-nums">
                {s.n}
              </p>
              <p className="mt-2 text-xs text-[#465B70]">
                Out of {claimed} founders
              </p>
            </CardContent>
          </Card>
        )).slice(0, 2)}
      </div>

      {error && (
        <div
          className="rounded-2xl border border-[#9b1c1c]/30 bg-[#9b1c1c]/8 px-4 py-2.5 text-sm text-[#9b1c1c]"
          data-testid="founder-error"
        >
          {error}
        </div>
      )}

      {/* Founders table */}
      <Card>
        <CardContent className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="founder-table">
              <thead className="bg-[#F5F2EB] text-left text-xs uppercase tracking-wide text-[#465B70]">
                <tr>
                  <th className="px-4 py-3">#</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Signed up</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 min-w-[240px]">Note</th>
                  <th className="px-4 py-3 text-right">Save</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-[#465B70]">
                      <Loader2 className="inline-block h-5 w-5 animate-spin mr-2" />
                      Loading founders…
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-[#465B70]" data-testid="founder-empty">
                      No founder registrations yet. The first 50 customers who register will appear here.
                    </td>
                  </tr>
                )}
                {!loading && rows.map((r) => {
                  const d = draft[r.user_id] || { status: r.status, note: r.note || '', dirty: false };
                  const tone =
                    STATUS_OPTS.find((s) => s.value === d.status)?.tone || STATUS_OPTS[0].tone;
                  return (
                    <tr key={r.user_id} className="border-t border-[#0A4D8C12] hover:bg-[#FDFBF7]" data-testid={`founder-row-${r.founder_no}`}>
                      <td className="px-4 py-3 font-display font-extrabold text-[#F25C05] tabular-nums">
                        {String(r.founder_no).padStart(2, '0')}
                      </td>
                      <td className="px-4 py-3 font-bold text-[#05223D]">{r.name}</td>
                      <td className="px-4 py-3 text-[#465B70]">{r.email}</td>
                      <td className="px-4 py-3 text-[#465B70]">{r.phone || '—'}</td>
                      <td className="px-4 py-3 text-[#465B70] whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      <td className="px-4 py-3">
                        <Select value={d.status} onValueChange={(v) => setField(r.user_id, 'status', v)}>
                          <SelectTrigger className={`h-9 w-[140px] rounded-lg border ${tone} font-bold`} data-testid={`founder-row-${r.founder_no}-status`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTS.map((s) => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <Input
                          value={d.note}
                          onChange={(e) => setField(r.user_id, 'note', e.target.value)}
                          placeholder="Carrier / tracking number / notes"
                          className="h-9 rounded-lg"
                          data-testid={`founder-row-${r.founder_no}-note`}
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          disabled={!d.dirty || savingId === r.user_id}
                          onClick={() => save(r.user_id)}
                          data-testid={`founder-row-${r.founder_no}-save`}
                          className="gap-1.5 bg-[#0A4D8C] hover:bg-[#063566]"
                        >
                          {savingId === r.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                          Save
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {remaining === 0 && rows.length > 0 && (
        <div className="rounded-2xl border border-[#0A4D8C]/20 bg-white p-4 text-sm text-[#465B70]" data-testid="founder-closed-notice">
          <Badge className="bg-[#F25C05] hover:bg-[#F25C05] mr-2">Closed</Badge>
          All 50 founder spots are claimed. The public campaign banner and registration badge are now hidden on the live site.
        </div>
      )}
    </div>
  );
}
